// HomePage/RoleManager.js
import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, 
  Switch, Card, Space, Popconfirm, message, Tag,
  Tabs, Alert, Tooltip, Badge, Row, Col,
  Drawer, Divider, Descriptions, Typography,
  Collapse, Statistic, Empty, Spin, Checkbox
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, ReloadOutlined, 
  UserOutlined, DatabaseOutlined, SettingOutlined,
  ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined,
  DashboardOutlined, SaveOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Available extra fields for roles
const AVAILABLE_EXTRA_FIELDS = [
  { value: 'designation', label: 'Designation', type: 'VARCHAR(255)' },
  { value: 'state_id', label: 'State ID', type: 'INT NULL' },
  { value: 'division_id', label: 'Division ID', type: 'INT NULL' },
  { value: 'district_id', label: 'District ID', type: 'INT NULL' },
  { value: 'ulb_id', label: 'ULB ID', type: 'INT NULL' },
  { value: 'ulb_name', label: 'ULB Name', type: 'VARCHAR(255)' },
  { value: 'ulb_type', label: 'ULB Type', type: 'VARCHAR(100)' },
  { value: 'district_admin_id', label: 'District Admin ID', type: 'INT NULL' },
  { value: 'phone_no', label: 'Phone Number', type: 'VARCHAR(20)' },
  { value: 'aadhar_number', label: 'Aadhar Number', type: 'VARCHAR(12)' },
];

// Available access permissions
const AVAILABLE_ACCESS = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'users', label: 'User Management' },
  { value: 'reports', label: 'Reports & Analytics' },
  { value: 'forms', label: 'Forms Builder' },
  { value: 'workflow', label: 'Validation Flow' },
  { value: 'ai_assistant', label: 'AI Assistant' },
  { value: 'graphs', label: 'Graphs' },
  { value: 'helpdesk', label: 'Helpdesk' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'user_transfer', label: 'User Transfer' },
  { value: 'ticket_system', label: 'Ticket System' },
  { value: 'admin_portal', label: 'Admin Portal' },
  { value: 'settings', label: 'Settings' },
];

// Dashboard Parts List
const DASHBOARD_PARTS = [
  { key: 'show_front_page_filters', label: 'Front Page Filters', icon: '🔍' },
  { key: 'show_normal_card_view', label: 'Normal Cards View', icon: '📇' },
  { key: 'show_compact_card_view', label: 'Compact Cards View', icon: '📋' },
  { key: 'show_data_point_view', label: 'Data Point View', icon: '📊' },
  { key: 'show_report_cards', label: 'Report Cards (KPI)', icon: '📈' },
  { key: 'show_analysis_section', label: 'Analysis Section', icon: '🧮' },
  { key: 'show_joint_graph', label: 'Joint Graph', icon: '📉' },
  { key: 'show_division_wise_analysis', label: 'Division Wise Analysis', icon: '🏛️' },
  { key: 'show_ranking_table', label: 'Ranking Table', icon: '🏆' },
  { key: 'show_key_metrics_summary', label: 'Key Metrics Summary', icon: '📌' },
  { key: 'show_row_data_table', label: 'Row Data Table', icon: '📄' },
];

// Protected roles
const PROTECTED_ROLES = ['SUPER_ADMIN', 'DMA_ADMIN', 'DMA_DESK_OFFICER', 'DMA_DESK_USER', 'ULB', 'DISTRICT_ADMIN', 'DIVISIONAL_OFFICER'];

const RoleManager = ({ onBack }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [userCount, setUserCount] = useState(0);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('roles');
  
  // Dashboard Config State
  const [roleDashboardConfigs, setRoleDashboardConfigs] = useState({});
  const [selectedConfigRole, setSelectedConfigRole] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  // Load Roles
  const loadRoles = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      message.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  // Load Role Dashboard Configs
  const loadRoleDashboardConfigs = async () => {
    setConfigLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/role-dashboard-configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const configMap = {};
        response.data.roleConfigs.forEach(item => {
          configMap[item.role_name] = item.config;
        });
        setRoleDashboardConfigs(configMap);
        
        // Set first role as selected
        if (response.data.roleConfigs.length > 0 && !selectedConfigRole) {
          setSelectedConfigRole(response.data.roleConfigs[0].role_name);
        }
      }
    } catch (error) {
      console.error('Error loading role dashboard configs:', error);
      message.error('Failed to load dashboard configs');
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
    loadRoleDashboardConfigs();
  }, []);

  // ==================== ROLE CRUD FUNCTIONS ====================
  
  const handleCreate = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      has_extra: [],
      allowed_access: []
    });
    setModalVisible(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    form.setFieldsValue({
      role_name: role.role_name,
      display_name: role.display_name,
      table_name: role.table_name,
      has_extra: role.has_extra || [],
      allowed_access: role.allowed_access || [],
      is_active: role.is_active === 1
    });
    setModalVisible(true);
  };

  const handleView = async (role) => {
    setSelectedRole(role);
    setDrawerVisible(true);
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/roles/${role.id}/users-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUserCount(response.data.count);
      }
    } catch (error) {
      setUserCount(0);
    }
  };

  const handleDelete = async (roleId) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(`${API_BASE_URL}/api/admin/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        message.success(response.data.message);
        loadRoles();
        loadRoleDashboardConfigs();
        setDrawerVisible(false);
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      message.error(error.response?.data?.error || 'Failed to delete role');
    }
  };

  const handleToggle = async (roleId, currentStatus) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.patch(`${API_BASE_URL}/api/admin/roles/${roleId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        message.success(response.data.message);
        loadRoles();
        loadRoleDashboardConfigs();
      }
    } catch (error) {
      console.error('Error toggling role:', error);
      message.error('Failed to toggle role status');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = sessionStorage.getItem('token');
      
      const payload = {
        role_name: values.role_name.trim(),
        display_name: values.display_name.trim(),
        table_name: values.table_name.trim(),
        has_extra: values.has_extra || [],
        allowed_access: values.allowed_access || [],
        is_active: values.is_active !== false
      };

      let response;
      if (editingRole) {
        response = await axios.put(`${API_BASE_URL}/api/admin/roles/${editingRole.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API_BASE_URL}/api/admin/roles`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data.success) {
        message.success(response.data.message);
        setModalVisible(false);
        loadRoles();
        loadRoleDashboardConfigs();
      }
    } catch (error) {
      console.error('Error saving role:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to save role');
      }
    }
  };

  // ==================== DASHBOARD CONFIG FUNCTIONS ====================
  
  const handleConfigChange = (roleName, key, value) => {
    setRoleDashboardConfigs(prev => ({
      ...prev,
      [roleName]: {
        ...prev[roleName],
        [key]: value
      }
    }));
  };

  const handleSaveConfig = async () => {
    if (!selectedConfigRole) {
      message.warning('Please select a role');
      return;
    }
    
    setConfigSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const config = roleDashboardConfigs[selectedConfigRole];
      
      const response = await axios.post(`${API_BASE_URL}/api/admin/role-dashboard-config`,
        {
          role_name: selectedConfigRole,
          config: config
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        message.success(`Dashboard config saved for ${selectedConfigRole}`);
      }
    } catch (error) {
      console.error('Error saving config:', error);
      message.error('Failed to save dashboard config');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleResetConfig = async () => {
    if (!selectedConfigRole) return;
    
    // Reset to default (all true)
    const defaultConfig = {};
    DASHBOARD_PARTS.forEach(part => {
      defaultConfig[part.key] = true;
    });
    
    setRoleDashboardConfigs(prev => ({
      ...prev,
      [selectedConfigRole]: defaultConfig
    }));
    
    message.info(`Config reset to default for ${selectedConfigRole}`);
  };

  // ==================== COLUMNS ====================
  
  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Role Name',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.display_name}
          </Text>
        </div>
      )
    },
    {
      title: 'Table',
      dataIndex: 'table_name',
      key: 'table_name',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Dashboard Parts',
      key: 'dashboard_parts',
      render: (_, record) => {
        const config = roleDashboardConfigs[record.role_name];
        if (!config) return <Tag>Default</Tag>;
        
        const enabledCount = DASHBOARD_PARTS.filter(part => config[part.key] !== false).length;
        const totalCount = DASHBOARD_PARTS.length;
        
        return (
          <Tooltip title={`${enabledCount}/${totalCount} parts enabled`}>
            <Tag color={enabledCount === totalCount ? 'green' : 'orange'}>
              {enabledCount}/{totalCount}
            </Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_, record) => {
        const isProtected = PROTECTED_ROLES.includes(record.role_name);
        
        return (
          <Space size="small">
            <Tooltip title="View Details">
              <Button 
                icon={<EyeOutlined />} 
                size="small" 
                onClick={() => handleView(record)}
              />
            </Tooltip>
            
            <Tooltip title={isProtected ? 'Protected role - limited edit' : 'Edit Role'}>
              <Button 
                icon={<EditOutlined />} 
                size="small" 
                onClick={() => handleEdit(record)}
                disabled={isProtected}
              />
            </Tooltip>
            
            <Tooltip title={isProtected ? 'Protected role - cannot delete' : 'Delete Role'}>
              <Popconfirm
                title="Delete this role?"
                description="This will permanently delete the role and its table."
                onConfirm={() => handleDelete(record.id)}
                okText="Yes, Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                disabled={isProtected}
              >
                <Button 
                  icon={<DeleteOutlined />} 
                  size="small" 
                  danger
                  disabled={isProtected}
                />
              </Popconfirm>
            </Tooltip>
            
            <Tooltip title={record.is_active ? 'Deactivate Role' : 'Activate Role'}>
              <Button 
                size="small" 
                onClick={() => handleToggle(record.id, record.is_active)}
                style={{ 
                  color: record.is_active ? '#faad14' : '#52c41a',
                  borderColor: record.is_active ? '#faad14' : '#52c41a'
                }}
              >
                {record.is_active ? 'Deactivate' : 'Activate'}
              </Button>
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  // ==================== RENDER DASHBOARD CONFIG TAB ====================
  
  const renderDashboardConfigTab = () => {
    const config = selectedConfigRole ? roleDashboardConfigs[selectedConfigRole] : null;
    
    return (
      <div style={{ padding: '20px 0' }}>
        <Row gutter={[24, 24]}>
          {/* Left: Role Selector */}
          <Col span={6}>
            <Card title="Select Role" size="small">
              <Select
                placeholder="Choose a role"
                value={selectedConfigRole}
                onChange={setSelectedConfigRole}
                style={{ width: '100%' }}
                showSearch
                loading={configLoading}
                optionFilterProp="children"
              >
                {roles.filter(r => r.is_active).map(role => (
                  <Option key={role.role_name} value={role.role_name}>
                    <Space>
                      <span>{role.role_name}</span>
                      <Tag color={role.is_active ? 'green' : 'red'}>
                        {role.is_active ? 'Active' : 'Inactive'}
                      </Tag>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Card>
            
            <Card title="Quick Summary" size="small" style={{ marginTop: 16 }}>
              {roles.filter(r => r.is_active).slice(0, 10).map(role => {
                const roleConfig = roleDashboardConfigs[role.role_name];
                const enabledCount = roleConfig 
                  ? DASHBOARD_PARTS.filter(part => roleConfig[part.key] !== false).length 
                  : DASHBOARD_PARTS.length;
                const totalCount = DASHBOARD_PARTS.length;
                
                return (
                  <div key={role.role_name} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    padding: '4px 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <Text style={{ fontSize: 12 }}>{role.role_name}</Text>
                    <Tag color={enabledCount === totalCount ? 'green' : 'orange'} style={{ fontSize: 10 }}>
                      {enabledCount}/{totalCount}
                    </Tag>
                  </div>
                );
              })}
            </Card>
          </Col>
          
          {/* Right: Config Editor */}
          <Col span={18}>
            <Card 
              title={
                <Space>
                  <DashboardOutlined />
                  <span>Dashboard Parts for: {selectedConfigRole || 'Select a role'}</span>
                </Space>
              }
              extra={
                <Space>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleResetConfig}
                    disabled={!selectedConfigRole}
                  >
                    Reset to Default
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    onClick={handleSaveConfig}
                    loading={configSaving}
                    disabled={!selectedConfigRole}
                  >
                    Save for this Role
                  </Button>
                </Space>
              }
              size="small"
            >
              {!selectedConfigRole ? (
                <Empty description="Please select a role to configure" />
              ) : !config ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <Spin />
                  <div style={{ marginTop: 12 }}>Loading config...</div>
                </div>
              ) : (
                <div>
                  <Alert
                    message={`Configuring: ${selectedConfigRole}`}
                    description={
                      <div>
                        Toggle the switches below to control which dashboard parts this role can see.
                        {PROTECTED_ROLES.includes(selectedConfigRole) && (
                          <Tag color="red" style={{ marginLeft: 8 }}>Protected Role</Tag>
                        )}
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  <Row gutter={[16, 16]}>
                    {DASHBOARD_PARTS.map(part => (
                      <Col span={12} key={part.key}>
                        <div style={{
                          padding: '12px 16px',
                          background: '#fafafa',
                          borderRadius: '8px',
                          border: '1px solid #f0f0f0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Space>
                            <span style={{ fontSize: 18 }}>{part.icon}</span>
                            <Text>{part.label}</Text>
                          </Space>
                          <Switch
                            checked={config[part.key] !== false}
                            onChange={(checked) => handleConfigChange(selectedConfigRole, part.key, checked)}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                  
                  <div style={{ marginTop: 16, padding: '12px', background: '#f6ffed', borderRadius: '8px' }}>
                    <Text type="secondary">
                      💡 <strong>Tip:</strong> Unchecking a part will hide it for ALL users with this role.
                    </Text>
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  
  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '20px' }}>
      {/* Header */}
      <div style={{ 
        background: 'white', 
        padding: '16px 24px', 
        borderRadius: '12px', 
        marginBottom: '20px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} size="large">
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            <SettingOutlined style={{ marginRight: '10px', color: '#0d47a1' }} />
            Role Manager
          </Title>
          <Badge count={roles.filter(r => r.is_active).length} showZero style={{ backgroundColor: '#0d47a1' }} />
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => {
              loadRoles();
              loadRoleDashboardConfigs();
            }}
            loading={loading || configLoading}
          >
            Refresh
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreate}
            style={{ background: '#0d47a1' }}
          >
            Create New Role
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={<span><UserOutlined /> Roles List</span>} 
            key="roles"
          >
            <div style={{ marginBottom: 16 }}>
              <Input
                placeholder="Search roles..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                size="large"
                style={{ maxWidth: 400 }}
              />
            </div>
            
            <Table
              columns={columns}
              dataSource={roles.filter(r => 
                r.role_name.toLowerCase().includes(searchText.toLowerCase()) ||
                r.display_name.toLowerCase().includes(searchText.toLowerCase())
              )}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} roles`
              }}
              scroll={{ x: 1000 }}
            />
            
            <div style={{ marginTop: 16 }}>
              <Alert
                message="Protected Roles"
                description={
                  <div>
                    <Text strong>Protected roles cannot be deleted or have their name changed:</Text>
                    <div style={{ marginTop: '8px' }}>
                      {PROTECTED_ROLES.map(role => (
                        <Tag key={role} color="red" style={{ marginBottom: '4px' }}>
                          {role}
                        </Tag>
                      ))}
                    </div>
                  </div>
                }
                type="info"
                showIcon
              />
            </div>
          </TabPane>
          
          <TabPane 
            tab={<span><DashboardOutlined /> Dashboard Config</span>} 
            key="dashboard"
          >
            {renderDashboardConfigTab()}
          </TabPane>
        </Tabs>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={
          <div>
            <SettingOutlined style={{ marginRight: '8px', color: '#0d47a1' }} />
            {editingRole ? `Edit Role: ${editingRole.role_name}` : 'Create New Role'}
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleSubmit}
            style={{ background: '#0d47a1' }}
          >
            {editingRole ? 'Update Role' : 'Create Role'}
          </Button>
        ]}
      >
        <Form form={form} layout="vertical" size="large">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="role_name" 
                label="Role Name (Identifier)"
                rules={[
                  { required: true, message: 'Please enter role name' },
                  { pattern: /^[A-Z_]+$/, message: 'Use uppercase letters and underscores only' }
                ]}
                tooltip="Used in code and JWT tokens. Use uppercase with underscores."
              >
                <Input placeholder="e.g., CITY_MANAGER" disabled={editingRole?.role_name && PROTECTED_ROLES.includes(editingRole.role_name)} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="display_name" 
                label="Display Name"
                rules={[{ required: true, message: 'Please enter display name' }]}
                tooltip="Shown in dropdowns and UI"
              >
                <Input placeholder="e.g., City Manager" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="table_name" 
            label="Database Table Name"
            rules={[
              { required: true, message: 'Please enter table name' },
              { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: 'Use letters, numbers, and underscores only' }
            ]}
            tooltip="Changing this will RENAME the database table"
          >
            <Input placeholder="e.g., city_manager" disabled={editingRole?.role_name && PROTECTED_ROLES.includes(editingRole.role_name)} />
          </Form.Item>

          <Divider />

          <Form.Item 
            name="has_extra" 
            label="Extra Fields"
            tooltip="Select additional columns for this role's table"
          >
            <Select 
              mode="multiple" 
              placeholder="Select fields..."
              style={{ width: '100%' }}
              optionFilterProp="label"
            >
              {AVAILABLE_EXTRA_FIELDS.map(field => (
                <Option key={field.value} value={field.value}>
                  {field.label} <Text type="secondary" style={{ fontSize: 11 }}>({field.type})</Text>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Alert 
            message="⚠️ Adding/removing fields will ALTER the database table"
            type="warning" 
            showIcon 
            style={{ marginBottom: '16px' }}
          />

          <Form.Item 
            name="allowed_access" 
            label="Access Permissions"
            tooltip="Controls what menu items this role can see"
          >
            <Select 
              mode="multiple" 
              placeholder="Select menu access..."
              style={{ width: '100%' }}
              optionFilterProp="label"
            >
              {AVAILABLE_ACCESS.map(access => (
                <Option key={access.value} value={access.value}>
                  {access.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_active" valuePropName="checked" label="Status">
            <Switch 
              checkedChildren="Active" 
              unCheckedChildren="Inactive" 
              defaultChecked
            />
          </Form.Item>

          {editingRole?.role_name && PROTECTED_ROLES.includes(editingRole.role_name) && (
            <Alert 
              message="This is a protected role. Some fields cannot be modified."
              type="info"
              showIcon
            />
          )}
        </Form>
      </Modal>

      {/* View Role Drawer */}
      <Drawer
        title={
          <div>
            <EyeOutlined style={{ marginRight: '8px' }} />
            Role Details: {selectedRole?.role_name}
          </div>
        }
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={550}
        extra={
          <Space>
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              onClick={() => {
                setDrawerVisible(false);
                handleEdit(selectedRole);
              }}
              disabled={selectedRole?.role_name && PROTECTED_ROLES.includes(selectedRole.role_name)}
            >
              Edit
            </Button>
          </Space>
        }
      >
        {selectedRole && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Tag color={selectedRole.is_active ? 'green' : 'red'} style={{ fontSize: '14px', padding: '4px 12px' }}>
                {selectedRole.is_active ? 'Active' : 'Inactive'}
              </Tag>
              {PROTECTED_ROLES.includes(selectedRole.role_name) && (
                <Tag color="red" style={{ fontSize: '14px', padding: '4px 12px', marginLeft: '8px' }}>
                  Protected
                </Tag>
              )}
            </div>

            <Descriptions bordered column={1} size="middle">
              <Descriptions.Item label="Role Name">
                <Text code style={{ fontSize: '16px' }}>{selectedRole.role_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Display Name">
                <Text strong>{selectedRole.display_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Table Name">
                <Tag color="blue">{selectedRole.table_name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(selectedRole.created_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {new Date(selectedRole.updated_at).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Users Count">
                <Badge count={userCount} showZero style={{ backgroundColor: '#0d47a1' }} />
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Card title="Extra Fields" size="small">
              {selectedRole.has_extra && selectedRole.has_extra.length > 0 ? (
                <div>
                  {selectedRole.has_extra.map(field => {
                    const fieldInfo = AVAILABLE_EXTRA_FIELDS.find(f => f.value === field);
                    return (
                      <Tag key={field} color="green" style={{ marginBottom: '4px', padding: '4px 8px' }}>
                        {fieldInfo?.label || field}
                      </Tag>
                    );
                  })}
                </div>
              ) : (
                <Text type="secondary">No extra fields configured</Text>
              )}
            </Card>

            <Card title="Access Permissions" size="small" style={{ marginTop: '12px' }}>
              {selectedRole.allowed_access && selectedRole.allowed_access.length > 0 ? (
                <div>
                  {selectedRole.allowed_access.map(access => {
                    const accessInfo = AVAILABLE_ACCESS.find(a => a.value === access);
                    return (
                      <Tag key={access} color="purple" style={{ marginBottom: '4px', padding: '4px 8px' }}>
                        {accessInfo?.label || access}
                      </Tag>
                    );
                  })}
                </div>
              ) : (
                <Text type="secondary">No access permissions configured</Text>
              )}
            </Card>

            <Card title="Dashboard Parts" size="small" style={{ marginTop: '12px' }}>
              {roleDashboardConfigs[selectedRole.role_name] ? (
                <div>
                  {DASHBOARD_PARTS.map(part => {
                    const isEnabled = roleDashboardConfigs[selectedRole.role_name][part.key] !== false;
                    return (
                      <Tag key={part.key} color={isEnabled ? 'green' : 'red'} style={{ marginBottom: '4px', padding: '4px 8px' }}>
                        {part.icon} {part.label} {isEnabled ? '✅' : '❌'}
                      </Tag>
                    );
                  })}
                </div>
              ) : (
                <Text type="secondary">All parts enabled (default)</Text>
              )}
            </Card>

            {PROTECTED_ROLES.includes(selectedRole.role_name) && (
              <Alert
                message="Protected Role"
                description="This is a system-protected role. It cannot be deleted or have its name changed."
                type="warning"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default RoleManager;